/**
 * HOSTED SESSION CLIENT (Phase 13A).
 *
 * The desktop application has no session — it is the owner's own machine.
 * The hosted build asks the server who it is talking to; if there is no
 * server, the application behaves exactly as it always has.
 */

export interface HostedUser {
  /** The signed-in account's own id (spec §66R). */
  id?: string;
  email: string;
  displayName: string;
  /** Phase 13B: the server decides this; the client only displays it. */
  role?: "admin" | "tester";
}

export interface HostedSession {
  mode: "server";
  user: HostedUser | null;
  /** The page came from the deployment, but the server is not answering. */
  unreachable?: boolean;
  /** Build identifier, shown in diagnostics and feedback. */
  version?: string;
}

/**
 * Ask the server for the current session. Null means "not hosted" — the
 * desktop shell or the development preview.
 *
 * If the page itself was served over http(s) and the request fails at the
 * network level, the deployment is there but its server has stopped (the
 * window was closed, the machine slept). That is reported, not hidden as
 * "not hosted", so the user gets a sentence they can act on.
 */
export async function fetchSession(): Promise<HostedSession | null> {
  try {
    const response = await fetch("/api/session", { credentials: "same-origin" });
    if (!response.ok) return null;
    const payload = (await response.json()) as Partial<HostedSession>;
    if (payload.mode !== "server") return null;
    rememberHosted();
    return { mode: "server", user: payload.user ?? null, version: payload.version };
  } catch {
    return wasHosted() ? { mode: "server", user: null, unreachable: true } : null;
  }
}

/**
 * A page this browser has already used the deployment from is remembered, so
 * a later network failure can be reported as "the server stopped" rather than
 * mistaken for the desktop shell. Nothing about the tester is stored — just
 * that this address is a deployment.
 */
const HOSTED_KEY = "berrier.deployment";

function rememberHosted(): void {
  try {
    window.localStorage.setItem(HOSTED_KEY, "1");
  } catch {
    /* private mode or storage disabled: the flag is a convenience only */
  }
}

function wasHosted(): boolean {
  try {
    return window.localStorage.getItem(HOSTED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The server is a window on the user's own machine: when it is closed, the
 * browser reports a bare "Failed to fetch", which explains nothing. Say what
 * actually happened instead.
 */
export const SERVER_UNREACHABLE =
  "Berrier Reno cannot reach its server. Check your internet connection, then try again.";

export async function login(email: string, password: string): Promise<HostedUser> {
  let response: Response;
  try {
    response = await fetch("/api/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new Error(SERVER_UNREACHABLE);
  }
  const payload = (await response.json().catch(() => ({}))) as { user?: HostedUser; error?: string };
  if (!response.ok || !payload.user) {
    throw new Error(payload.error ?? "That sign-in could not be completed.");
  }
  return payload.user;
}

export async function logout(): Promise<void> {
  await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
}

/* ------------------------------------------------------------------ */
/* Early Access (spec §86)                                             */
/* ------------------------------------------------------------------ */

/** What the server says about the signed-in account. The client displays it; the server decides it. */
export interface AccountState {
  profile: { companyName: string; experienceLevel: "" | "new" | "some" | "experienced"; primaryIntent: "" | "flip" | "rental" | "other"; signupSource: "invited" | "public" };
  onboardingComplete: boolean;
  access: {
    allowed: boolean;
    source: "owner" | "complimentary_beta" | "stripe_subscription" | "none";
    subscriptionStatus: string;
    warning: "payment_past_due" | "cancels_at_period_end" | null;
    currentPeriodEnd: string | null;
    enforced: boolean;
    explanation: string;
  };
  billing: { configured: boolean; canManage: boolean };
  legal: {
    terms: { current: string; accepted: string | null };
    privacy: { current: string; accepted: string | null };
    needsAcceptance: boolean;
  };
}

export type HostedAccount = HostedUser & Partial<AccountState>;

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error ?? "That did not work. Try again.");
  return payload;
}

export async function saveProfile(input: {
  displayName?: string;
  companyName?: string;
  experienceLevel?: string;
  primaryIntent?: string;
  completeOnboarding?: boolean;
}): Promise<AccountState> {
  return (await postJson<{ state: AccountState }>("/api/account/profile", input)).state;
}

export async function acceptLegal(): Promise<AccountState> {
  return (await postJson<{ state: AccountState }>("/api/legal/accept", { terms: true, privacy: true })).state;
}

export async function startCheckout(): Promise<string> {
  return (await postJson<{ url: string }>("/api/billing/checkout")).url;
}

export async function openBillingPortal(): Promise<string> {
  return (await postJson<{ url: string }>("/api/billing/portal")).url;
}

export async function refreshBilling(): Promise<AccountState> {
  return postJson<AccountState>("/api/billing/refresh");
}

export async function requestSignup(email: string, displayName: string, acceptTerms: boolean): Promise<string> {
  return (await postJson<{ message: string }>("/api/signup", { email, displayName, acceptTerms })).message;
}

export interface PublicConfig {
  signupEnabled: boolean;
  billing: { configured: boolean; required: boolean };
}

export async function fetchPublicConfig(): Promise<PublicConfig | null> {
  try {
    const response = await fetch("/api/public/config", { credentials: "same-origin" });
    if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) return null;
    return (await response.json()) as PublicConfig;
  } catch {
    return null;
  }
}

export interface PublicPricing {
  configured: boolean;
  amountCents: number | null;
  currency: string | null;
  interval: string | null;
  intervalCount: number | null;
  productName: string | null;
}

export async function fetchPricing(): Promise<PublicPricing | null> {
  try {
    const response = await fetch("/api/public/pricing", { credentials: "same-origin" });
    if (!response.ok) return null;
    return (await response.json()) as PublicPricing;
  } catch {
    return null;
  }
}

/** "$49 per month" — from Stripe's own Price, never a number in this code. */
export function formatPrice(pricing: PublicPricing): string | null {
  if (pricing.amountCents === null || !pricing.currency) return null;
  const amount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: pricing.currency.toUpperCase(),
    minimumFractionDigits: pricing.amountCents % 100 === 0 ? 0 : 2,
  }).format(pricing.amountCents / 100);
  if (!pricing.interval) return amount;
  const every = (pricing.intervalCount ?? 1) > 1 ? `every ${pricing.intervalCount} ${pricing.interval}s` : `per ${pricing.interval}`;
  return `${amount} ${every}`;
}
